// IndexedDB wrapper for offline data storage
const DB_NAME = 'FarmGamificationDB';
const DB_VERSION = 1;

export interface Activity {
  id: string;
  type: string;
  crop?: string;
  date: Date;
  duration?: number;
  area?: number;
  notes?: string;
  photos?: string[];
  points: number;
  synced: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Photo {
  id: string;
  url: string;
  thumbnail?: string;
  activityId?: string;
  size: number;
  compressed: boolean;
  synced: boolean;
  createdAt: Date;
}

export interface VoiceRecording {
  id: string;
  audioBlob: Blob;
  transcript?: string;
  duration: number;
  category?: string;
  synced: boolean;
  createdAt: Date;
}

export interface ScoreHistory {
  id: string;
  totalPoints: number;
  level: number;
  timestamp: Date;
}

export interface OfflineDailyRecord {
  id: string;
  data: any;
  synced: boolean;
  createdAt: Date;
}

export interface NetworkPost {
  id: string;
  author: string;
  content: string;
  category: string;
  engagement: {
    replies: number;
    helpful: number;
  };
  timestamp: Date;
  cachedAt: Date;
}

class OfflineStorageService {
  private db: IDBDatabase | null = null;
  private readonly MAX_CACHE_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days
  private readonly MAX_STORAGE_SIZE = 50 * 1024 * 1024; // 50MB

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Activities store
        if (!db.objectStoreNames.contains('activities')) {
          const activityStore = db.createObjectStore('activities', { keyPath: 'id' });
          activityStore.createIndex('synced', 'synced', { unique: false });
          activityStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Photos store
        if (!db.objectStoreNames.contains('photos')) {
          const photoStore = db.createObjectStore('photos', { keyPath: 'id' });
          photoStore.createIndex('synced', 'synced', { unique: false });
          photoStore.createIndex('activityId', 'activityId', { unique: false });
        }

        // Voice recordings store
        if (!db.objectStoreNames.contains('voiceRecordings')) {
          const voiceStore = db.createObjectStore('voiceRecordings', { keyPath: 'id' });
          voiceStore.createIndex('synced', 'synced', { unique: false });
        }

        // Score history store
        if (!db.objectStoreNames.contains('scoreHistory')) {
          const scoreStore = db.createObjectStore('scoreHistory', { keyPath: 'id' });
          scoreStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Network posts cache
        if (!db.objectStoreNames.contains('networkPosts')) {
          const networkStore = db.createObjectStore('networkPosts', { keyPath: 'id' });
          networkStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        }

        // Daily records store
        if (!db.objectStoreNames.contains('dailyRecords')) {
          const dailyRecordStore = db.createObjectStore('dailyRecords', { keyPath: 'id' });
          dailyRecordStore.createIndex('synced', 'synced', { unique: false });
        }
      };
    });
  }

  private getStore(storeName: string, mode: IDBTransactionMode = 'readonly'): IDBObjectStore {
    if (!this.db) throw new Error('Database not initialized');
    const transaction = this.db.transaction(storeName, mode);
    return transaction.objectStore(storeName);
  }

  // Generic CRUD operations
  async add<T>(storeName: string, data: T): Promise<void> {
    const store = this.getStore(storeName, 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.add(data);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async get<T>(storeName: string, id: string): Promise<T | undefined> {
    const store = this.getStore(storeName);
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAll<T>(storeName: string): Promise<T[]> {
    const store = this.getStore(storeName);
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async update<T>(storeName: string, data: T): Promise<void> {
    const store = this.getStore(storeName, 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName: string, id: string): Promise<void> {
    const store = this.getStore(storeName, 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getByIndex<T>(storeName: string, indexName: string, value: any): Promise<T[]> {
    const store = this.getStore(storeName);
    const index = store.index(indexName);
    return new Promise((resolve, reject) => {
      const request = index.getAll(value);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Activity-specific methods
  async addActivity(activity: Activity): Promise<void> {
    return this.add('activities', activity);
  }

  async getUnsyncedActivities(): Promise<Activity[]> {
    return this.getByIndex('activities', 'synced', false);
  }

  async markActivitySynced(id: string): Promise<void> {
    const activity = await this.get<Activity>('activities', id);
    if (activity) {
      activity.synced = true;
      activity.updatedAt = new Date();
      await this.update('activities', activity);
    }
  }

  // Photo-specific methods
  async addPhoto(photo: Photo): Promise<void> {
    return this.add('photos', photo);
  }

  async getUnsyncedPhotos(): Promise<Photo[]> {
    return this.getByIndex('photos', 'synced', false);
  }

  // Voice recording methods
  async addVoiceRecording(recording: VoiceRecording): Promise<void> {
    return this.add('voiceRecordings', recording);
  }

  async getUnsyncedVoiceRecordings(): Promise<VoiceRecording[]> {
    return this.getByIndex('voiceRecordings', 'synced', false);
  }

  // Daily record methods
  async addDailyRecord(record: OfflineDailyRecord): Promise<void> {
    return this.add('dailyRecords', record);
  }

  async getUnsyncedDailyRecords(): Promise<OfflineDailyRecord[]> {
    return this.getByIndex('dailyRecords', 'synced', false);
  }

  async markDailyRecordSynced(id: string): Promise<void> {
    const record = await this.get<OfflineDailyRecord>('dailyRecords', id);
    if (record) {
      record.synced = true;
      await this.update('dailyRecords', record);
    }
  }

  // Data size monitoring
  async getStorageSize(): Promise<number> {
    if (!navigator.storage || !navigator.storage.estimate) {
      return 0;
    }

    const estimate = await navigator.storage.estimate();
    return estimate.usage || 0;
  }

  async isStorageAvailable(requiredSize: number): Promise<boolean> {
    const currentSize = await this.getStorageSize();
    return currentSize + requiredSize < this.MAX_STORAGE_SIZE;
  }

  // Cleanup old cached data
  async cleanupOldCache(): Promise<void> {
    const cutoffDate = new Date(Date.now() - this.MAX_CACHE_AGE);
    
    // Clean old network posts
    const posts = await this.getAll<NetworkPost>('networkPosts');
    const oldPosts = posts.filter(p => p.cachedAt < cutoffDate);
    
    for (const post of oldPosts) {
      await this.delete('networkPosts', post.id);
    }

    console.log(`Cleaned up ${oldPosts.length} old cached posts`);
  }

  async clearAllData(): Promise<void> {
    const stores = ['activities', 'photos', 'voiceRecordings', 'scoreHistory', 'networkPosts', 'dailyRecords'];
    
    for (const storeName of stores) {
      const store = this.getStore(storeName, 'readwrite');
      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve(undefined);
        request.onerror = () => reject(request.error);
      });
    }
  }
}

export const offlineStorage = new OfflineStorageService();
